/** Restart the scene unless the playhead is still at its opening. */
const SCENE_SKIP_RESTART_MS = 400;
/** Keep the playhead still so rapid skip clicks can chain while playing. */
export const SCENE_SKIP_HOLD_MS = 225;

interface SkipBounds {
  startMs: number;
  endMs: number;
}

export function skipScenePlayhead(
  beats: readonly { startMs: number }[],
  playheadMs: number,
  direction: -1 | 1,
  bounds: SkipBounds,
): number {
  const starts = sceneStartsInBounds(beats, bounds);
  if (direction === 1) {
    const next = starts.find((ms) => ms > playheadMs + 1);
    return clampPlayhead(next ?? bounds.endMs, bounds);
  }
  const current = currentSceneStart(starts, playheadMs, bounds.startMs);
  if (playheadMs - current > SCENE_SKIP_RESTART_MS) {
    return clampPlayhead(current, bounds);
  }
  let previous = bounds.startMs;
  for (const ms of starts) {
    if (ms < current) {
      previous = ms;
    }
  }
  return clampPlayhead(previous, bounds);
}

function sceneStartsInBounds(
  beats: readonly { startMs: number }[],
  bounds: SkipBounds,
): number[] {
  const starts: number[] = [];
  for (const beat of beats) {
    if (beat.startMs >= bounds.startMs && beat.startMs < bounds.endMs) {
      starts.push(beat.startMs);
    }
  }
  return starts;
}

function currentSceneStart(
  starts: readonly number[],
  playheadMs: number,
  fallback: number,
): number {
  let current = fallback;
  for (const ms of starts) {
    if (ms <= playheadMs) {
      current = ms;
    }
  }
  return current;
}

function clampPlayhead(playheadMs: number, bounds: SkipBounds): number {
  return Math.min(bounds.endMs, Math.max(bounds.startMs, playheadMs));
}
